import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Snackbar,
  FormControl,
  Select,
  MenuItem,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { UniversalPagination } from '../common/UniversalPagination';
import { getClients, getClientDetails } from '../../services/client.service';
import { Client, ClientDetails, PaginatedClientsResponse, GetClientsParams } from '../../types/client';
import { ClientCard } from '../clients/ClientCard';
import { ClientDetailsModal } from '../clients/ClientDetailsModal';

interface ExpertDashboardClientsCardListProps {
  connectionStatus: string;
  title: string;
  subtitle?: string;
}

const ExpertDashboardClientsCardList: React.FC<ExpertDashboardClientsCardListProps> = ({
  connectionStatus,
  title,
  subtitle,
}) => {
  const [clientsResponse, setClientsResponse] = useState<PaginatedClientsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [searchParams, setSearchParams] = useState<GetClientsParams>(() => {
    const savedLimit = localStorage.getItem('pagination:dashboard-clients');
    return { 
      page: 1, 
      limit: savedLimit ? Number(savedLimit) : 12, 
      connectionStatus 
    };
  });

  const [selectedClientDetails, setSelectedClientDetails] = useState<ClientDetails | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getClients(searchParams);
      setClientsResponse(response);
    } catch (err: any) {
      const backendMessage = err.response?.data?.message;
      const errorMessage = Array.isArray(backendMessage) ? backendMessage.join(', ') : backendMessage || 'Не вдалося завантажити список клієнтів.';
      setError(errorMessage);
      console.error('Failed to fetch clients:', err);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleViewDetails = async (client: Client) => {
    try {
      const details = await getClientDetails(client.id);
      setSelectedClientDetails(details);
      setIsDetailsModalOpen(true);
    } catch (err) {
      setError('Не вдалося завантажити детальну інформацію про клієнта.');
      console.error('Failed to fetch client details', err);
    }
  };
  
  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setTimeout(() => {
      setSelectedClientDetails(null);
    }, 300);
  };

  const handlePageChange = (value: number) => {
    setSearchParams(prev => ({ ...prev, page: value }));
  };

  const handleLimitChange = (value: number) => {
    localStorage.setItem('pagination:dashboard-clients', String(value));
    setSearchParams(prev => ({ ...prev, limit: value, page: 1 }));
  };

  const LIMIT_OPTIONS = [6, 12, 24, 48];

  const renderContent = () => {
    if (loading) {
      return <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box>;
    }

    if (error && (!clientsResponse || clientsResponse.clients.length === 0)) {
        return null;
    }

    if (!clientsResponse || clientsResponse.clients.length === 0) {
      return <Typography sx={{ mt: 4, textAlign: 'center' }}>Клієнтів не знайдено.</Typography>;
    }

    return (
      <>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {clientsResponse.clients.map(client => (
            <Grid item key={client.id} xs={12} sm={6} md={4} lg={3} xl={2}>
              <ClientCard 
                client={client} 
                onViewDetails={handleViewDetails}
              />
            </Grid>
          ))}
        </Grid>
        
        {clientsResponse.pagination.pages > 1 && (
          <UniversalPagination
            page={clientsResponse.pagination.currentPage}
            count={clientsResponse.pagination.pages}
            limit={searchParams.limit!}
            onPageChange={handlePageChange}
            sx={{ mt: 4 }}
          />
        )}
      </>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: subtitle ? 0 : 2, width: '100%' }}>
        <Typography variant="h5" fontWeight={700}>{title}</Typography>
        <Link component={RouterLink} to="/clients" variant="caption" underline="hover" sx={{ ml: 1 }}>
          Всі клієнти
        </Link>
        
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
          {clientsResponse && (
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, opacity: 0.8 }}>
              Всього: <b>{clientsResponse.pagination.total}</b>
            </Typography>
          )}
          <FormControl size="small" variant="outlined">
            <Select
              value={searchParams.limit}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
              sx={{ 
                height: 28, 
                fontSize: '0.75rem',
                bgcolor: 'background.paper',
                '& .MuiSelect-select': { py: 0, pr: '24px !important' },
                borderRadius: 1.5,
                borderColor: 'rgba(0,0,0,0.1)'
              }}
            >
              {LIMIT_OPTIONS.map((n) => (
                <MenuItem key={n} value={n} sx={{ fontSize: '0.75rem' }}>
                  {n} / стор.
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {subtitle && <Typography paragraph sx={{ mt: 0.5, mb: 2, color: 'text.secondary', fontSize: '0.9rem' }}>{subtitle}</Typography>}

      {renderContent()}

      {selectedClientDetails && (
          <ClientDetailsModal 
              client={selectedClientDetails} 
              open={isDetailsModalOpen} 
              onClose={handleCloseDetailsModal}
          />
      )}
      
      <Snackbar 
          open={!!error} 
          autoHideDuration={6000} 
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
              {error}
          </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExpertDashboardClientsCardList;
